
import * as Enums from '../enums/index.js';
import BaseballResultsProcessor from '../entities/BaseballResultsProcessor.js';
import { ResultPair } from '../valueObjects/index.js';
import * as Classificators from '../classificators/index.js';
import * as Filters from '../filters/index.js'
import FilteredBattingResultsProvider from './FilteredBattingResultsProvider.js';

/**
 * Calculates player stats from results
 * @deprecated - not compatible with the new scoring nor extended tagging
 */
class CumulativePlayerStatsProvider {
    constructor(customResultsProcessor, filteredBattingResultsProvider) {
        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
        this.filteredBattingResultsProvider = filteredBattingResultsProvider || new FilteredBattingResultsProvider(this.resultsProcessor);

        if (!(this.resultsProcessor instanceof BaseballResultsProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from BaseballResultsProcessor class');
        }

        if (!(this.filteredBattingResultsProvider instanceof FilteredBattingResultsProvider)) {
            throw new TypeError('filteredBattingResultsProvider must be an instance inheriting from FilteredBattingResultsProvider class');
        }

        // default classifiers goes here... override by the property
        this.battedBallEventClassifier = Classificators.battedBallEventClassifier;
        this.fastballClassifier = Classificators.PitchTypeGroups.FastballClassifier;
        this.breakingBallClassifier = Classificators.PitchTypeGroups.BreakingBallClassifier;
        this.offSpeedClassifier = Classificators.PitchTypeGroups.OffSpeedClassifier;
    }

    defaultFilters() {
        return this.filteredBattingResultsProvider.defaultFilters();
    }

    getBattedBallEvents(resultPairs) {
        return this.resultsProcessor.filterData(
            resultPairs,
            [].concat(
                Filters.byParameterName.filterOnlyMatchingPredicate(
                    [ResultPair.getPitchProperty()].concat(Enums.ResultData.getDataPath(Enums.PitchData.Result.key)),
                    this.battedBallEventClassifier
                )
            )
        );
    }

    getHitsWithExitSpeedMeasured(hits) {
        return this.resultsProcessor.filterData(
            hits,
            [].concat(
                Filters.byParameterName.filterOnlyHavingParameterSet(
                    Enums.ResultData.getDataPath(Enums.HitData.ExitSpeed.key)
                )
            )
        );
    }

    getPitchesWithSpeedMeasured(pitches) {
        return this.resultsProcessor.filterData(
            pitches,
            [].concat(
                Filters.byParameterName.filterOnlyHavingParameterSet(
                    Enums.ResultData.getDataPath(Enums.PitchData.Speed.key)
                )
            )
        );
    }

    getPitchesWithSpinMeasured(pitches) {
        return this.resultsProcessor.filterData(
            pitches,
            [].concat(
                Filters.byParameterName.filterOnlyHavingParameterSet(
                    Enums.ResultData.getDataPath(Enums.PitchData.Spin.key)
                )
            )
        );
    }

    getPitchesWithPitchType(pitches) {
        return this.resultsProcessor.filterData(
            pitches,
            [].concat(
                Filters.byParameterName.filterOnlyNotHavingValueFromArray(
                    Enums.ResultData.getDataPath(Enums.PitchData.Classification.key),
                    [Enums.PitchType.Unidentified.key, Enums.PitchType.Other.key]
                )
            )
        );
    }

    getPitchesTaggedAsFastball(pitches) {
        return this.resultsProcessor.filterData(
            pitches,
            [].concat(
                Filters.byParameterName.filterOnlyMatchingPredicate(
                    Enums.ResultData.getDataPath(Enums.PitchData.Classification.key),
                    this.fastballClassifier
                )
            )
        );
    }

    getPitchesTaggedAsBreakingBall(pitches) {
        return this.resultsProcessor.filterData(
            pitches,
            [].concat(
                Filters.byParameterName.filterOnlyMatchingPredicate(
                    Enums.ResultData.getDataPath(Enums.PitchData.Classification.key),
                    this.breakingBallClassifier
                )
            )
        );
    }

    getPitchesTaggedAsOffSpeed(pitches) {
        return this.resultsProcessor.filterData(
            pitches,
            [].concat(
                Filters.byParameterName.filterOnlyMatchingPredicate(
                    Enums.ResultData.getDataPath(Enums.PitchData.Classification.key),
                    this.offSpeedClassifier
                )
            )
        );
    }

    calculateBattingStats(playerTagId, resultPairs, playerStats) {
        const filers = [].concat(Filters.byParameterValue.filterOnlyMatchingTagId(playerTagId));
        const filteredResultPairs = this.resultsProcessor.filterData(
            resultPairs, filers.map(f => {
                f.parameter = [ResultPair.getHitProperty()].concat(f.parameter);
                return f;
            })
        );
        const battedBallEvents = this.getBattedBallEvents(filteredResultPairs);
        const hitsMeasured = this.getHitsWithExitSpeedMeasured(this.filteredBattingResultsProvider.extractHits(battedBallEvents));
        if (hitsMeasured && hitsMeasured.length) {
            const battingStats = this.resultsProcessor.calcStats(
                [Enums.StatType.Avg.key, Enums.StatType.Max.key],
                hitsMeasured,
                // TODO - simplify it...
                [ [ [], Enums.ResultData.Data.key, Enums.HitData.ExitSpeed.key ] ],
                false
            );
            if (battingStats && battingStats.hasOwnProperty(Enums.HitData.ExitSpeed.key)) {
                playerStats.Batting[Enums.BattingStats.MaximumExitVelocity.key] = battingStats[Enums.HitData.ExitSpeed.key][Enums.StatType.Max.key] || NaN;
                playerStats.Batting[Enums.BattingStats.AverageExitVelocity.key] = battingStats[Enums.HitData.ExitSpeed.key][Enums.StatType.Avg.key] || NaN;
            }
        }

        return playerStats;
    }

    calculatePitchingStats(playerTagId, pitches, playerStats) {
        const filteredPitches = this.resultsProcessor.filterData(
            pitches, [].concat(Filters.byParameterValue.filterOnlyMatchingTagId(playerTagId))
        );
        const taggedPitches = this.getPitchesWithPitchType(filteredPitches);
        const pitchesWithSpeed = this.getPitchesWithSpeedMeasured(taggedPitches);
        const pitchesWithSpin = this.getPitchesWithSpinMeasured(taggedPitches);

        if (pitchesWithSpeed && pitchesWithSpeed.length) {
            // fastballs
            const fastballSpeedStats = this.resultsProcessor.calcStats(
                [Enums.StatType.Avg.key, Enums.StatType.Max.key],
                this.getPitchesTaggedAsFastball(pitchesWithSpeed),
                // TODO - simplify it...
                [ [ [], Enums.ResultData.Data.key, Enums.PitchData.Speed.key ] ],
                false
            );
            if (fastballSpeedStats && fastballSpeedStats.hasOwnProperty(Enums.PitchData.Speed.key)) {
                playerStats.Pitching[Enums.PitchingStats.MaximumFastballVelocity.key] = fastballSpeedStats[Enums.PitchData.Speed.key][Enums.StatType.Max.key] || NaN;
                playerStats.Pitching[Enums.PitchingStats.AverageFastballVelocity.key] = fastballSpeedStats[Enums.PitchData.Speed.key][Enums.StatType.Avg.key] || NaN;
            }

            // breaking balls
            const breakingBallSpeedStats = this.resultsProcessor.calcStats(
                [Enums.StatType.Avg.key],
                this.getPitchesTaggedAsBreakingBall(pitchesWithSpeed),
                // TODO - simplify it...
                [ [ [], Enums.ResultData.Data.key, Enums.PitchData.Speed.key ] ],
                false
            );
            if (breakingBallSpeedStats && breakingBallSpeedStats.hasOwnProperty(Enums.PitchData.Speed.key)) {
                playerStats.Pitching[Enums.PitchingStats.AverageBreakingBallVelocity.key] = breakingBallSpeedStats[Enums.PitchData.Speed.key][Enums.StatType.Avg.key] || NaN;
            }

            // Off-Speed balls
            const offSpeedSpeedStats = this.resultsProcessor.calcStats(
                [Enums.StatType.Avg.key],
                this.getPitchesTaggedAsOffSpeed(pitchesWithSpeed),
                // TODO - simplify it...
                [ [ [], Enums.ResultData.Data.key, Enums.PitchData.Speed.key ] ],
                false
            );
            if (offSpeedSpeedStats && offSpeedSpeedStats.hasOwnProperty(Enums.PitchData.Speed.key)) {
                playerStats.Pitching[Enums.PitchingStats.AverageOffSpeedVelocity.key] = offSpeedSpeedStats[Enums.PitchData.Speed.key][Enums.StatType.Avg.key] || NaN;
            }
        }

        if (pitchesWithSpin && pitchesWithSpin.length) {
            // fastballs
            const fastballSpinStats = this.resultsProcessor.calcStats(
                [Enums.StatType.Avg.key],
                this.getPitchesTaggedAsFastball(pitchesWithSpin),
                // TODO - simplify it...
                [ [ [], Enums.ResultData.Data.key, Enums.PitchData.Spin.key ] ],
                false
            );
            if (fastballSpinStats && fastballSpinStats.hasOwnProperty(Enums.PitchData.Spin.key)) {
                playerStats.Pitching[Enums.PitchingStats.AverageFastballSpin.key] = fastballSpinStats[Enums.PitchData.Spin.key][Enums.StatType.Avg.key] || NaN;
            }

            // breaking balls
            const breakingBallSpinStats = this.resultsProcessor.calcStats(
                [Enums.StatType.Avg.key],
                this.getPitchesTaggedAsBreakingBall(pitchesWithSpin),
                // TODO - simplify it...
                [ [ [], Enums.ResultData.Data.key, Enums.PitchData.Spin.key ] ],
                false
            );
            if (breakingBallSpinStats && breakingBallSpinStats.hasOwnProperty(Enums.PitchData.Spin.key)) {
                playerStats.Pitching[Enums.PitchingStats.AverageBreakingBallSpin.key] = breakingBallSpinStats[Enums.PitchData.Spin.key][Enums.StatType.Avg.key] || NaN;
            }

            // Off-Speed balls
            const offSpeedSpinStats = this.resultsProcessor.calcStats(
                [Enums.StatType.Avg.key],
                this.getPitchesTaggedAsOffSpeed(pitchesWithSpin),
                // TODO - simplify it...
                [ [ [], Enums.ResultData.Data.key, Enums.PitchData.Speed.key ] ],
                false
            );
            if (offSpeedSpinStats && offSpeedSpinStats.hasOwnProperty(Enums.PitchData.Spin.key)) {
                playerStats.Pitching[Enums.PitchingStats.AverageOffSpeedSpin.key] = offSpeedSpinStats[Enums.PitchData.Spin.key][Enums.StatType.Avg.key] || NaN;
            }
        }

        return playerStats;
    }

    getCumulativePlayerStats (
        results, playerTagId,
        {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        } = {}
    ) {
        if (!playerTagId || !Number.isInteger(playerTagId) || playerTagId < 1) {
            throw new TypeError('playerTagId must be an integer greater than 0');
        }

        const filteredPairs = this.filteredBattingResultsProvider.filterResults(results, {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        });

        const cumulativeStats = {
            Batting: {
                [Enums.BattingStats.MaximumExitVelocity.key]: NaN,
                [Enums.BattingStats.AverageExitVelocity.key]: NaN,
            },
            Pitching: {
                [Enums.PitchingStats.MaximumFastballVelocity.key]: NaN,
                [Enums.PitchingStats.AverageFastballVelocity.key]: NaN,
                [Enums.PitchingStats.AverageFastballSpin.key]: NaN,
                [Enums.PitchingStats.AverageBreakingBallVelocity.key]: NaN,
                [Enums.PitchingStats.AverageBreakingBallSpin.key]: NaN,
                [Enums.PitchingStats.AverageOffSpeedVelocity.key]: NaN,
                [Enums.PitchingStats.AverageOffSpeedSpin.key]: NaN,
            },
        };

        const filteredPitches = this.filteredBattingResultsProvider.extractPitches(filteredPairs);
        this.calculateBattingStats(playerTagId, filteredPairs, cumulativeStats);
        this.calculatePitchingStats(playerTagId, filteredPitches, cumulativeStats);

        return cumulativeStats;
    };
}

export default CumulativePlayerStatsProvider;